import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import { TipoOcorrencia } from '@acme/core'
import {
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
    FormFieldTime,
} from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useFormContext } from 'react-hook-form'
import { match } from 'ts-pattern'

export const OcorrenciaForm = () => {
    const { watch } = useFormContext()
    const tipo: TipoOcorrencia = watch('tipo')
    const feriadoNaSemana = watch('feriadoNaSemana')

    return (
        <Grid container>
            <Grid item xs={5}>
                <FormField
                    name="codigo"
                    label="Código"
                    placeholder="(gerar automaticamente)"
                    type="number"
                    endAdornment={
                        <IconeAjuda>
                            Código da ocorrência. Caso esteja em branco, o
                            sistema irá gerar um código automaticamente. Se
                            optar por informar um código, este não pode ter sido
                            utilizado anteriormente.
                        </IconeAjuda>
                    }
                    min={1}
                    max={9999}
                />
            </Grid>
            <Grid item xs={7}>
                <FormField name="descricao" required label="Nome" />
            </Grid>
            <Grid item xs={6}>
                <FormFieldSelect
                    name="tipo"
                    label="Tipo"
                    defaultValue={TipoOcorrencia.AFASTAMENTO}
                    required
                    options={[
                        {
                            value: TipoOcorrencia.AFASTAMENTO,
                            label: 'Afastamento',
                        },
                        {
                            value: TipoOcorrencia.COMPENSACAO,
                            label: 'Compensação',
                        },
                        {
                            value: TipoOcorrencia.AMAMENTACAO,
                            label: 'Amamentação',
                        },
                        { value: TipoOcorrencia.FOLGA, label: 'Folga' },
                    ]}
                    endAdornment={
                        <IconeAjuda>
                            <div className="max-h-74 overflow-auto">
                                Define como o sistema irá tratar os lançamentos
                                desta ocorrência.
                                <br />
                                <br />
                                <strong>Afastamento:</strong> Todas as faltas do
                                dia serão zeradas. O funcionário não pode
                                trabalhar no dia, mas se houver lançamento de
                                ponto, ele receberá horas extras.
                                <br />
                                <br />
                                <strong>Folga:</strong> Todas as faltas do dia
                                serão zeradas. O funcionário pode trabalhar no
                                dia e receberá horas extras.
                                <br />
                                <br />
                                <strong>Compensação:</strong> Faltas não serão
                                zeradas. Pode ou não pagar o vale alimentação.
                                Se houver feriado na semana, as horas extras
                                informadas poderão ser acrescidas no dia da
                                compensação.
                                <br />
                                <br />
                                <strong>Amamentação:</strong> Se as faltas do
                                dia forem menores ou iguais ao tempo de
                                amamentação, as faltas serão zeradas. Caso
                                contrário, as faltas serão reduzidas do tempo de
                                amamentação.
                            </div>
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <FormFieldCheckbox
                    name="somarHorasTrabalhar"
                    label="Somar horas a trabalhar"
                    endAdornment={
                        <IconeAjuda>
                            Caso marcado, um dia com essa ocorrência irá somar
                            horas a trabalhar ao invés de zerar, mesmo que o
                            tipo seja Afastamento.
                            <br />
                            <br />
                            Geralmente, compensação e amamentação são os tipos
                            de ocorrência que habilitam essa configuração, mas
                            ela pode ser aplicada a qualquer tipo.
                        </IconeAjuda>
                    }
                />
            </Grid>
            {[TipoOcorrencia.COMPENSACAO, TipoOcorrencia.AMAMENTACAO].includes(
                tipo as any,
            ) && (
                <Grid item xs={12}>
                    <HorizontalDivider className="select-none">
                        Configuração do tipo de ocorrência
                    </HorizontalDivider>
                </Grid>
            )}
            {match(tipo)
                .with(TipoOcorrencia.COMPENSACAO, () => [
                    <Grid item xs={12} sm={7} key="feriadoNaSemana">
                        <FormFieldCheckbox
                            name="feriadoNaSemana"
                            label="Feriado já compensado na semana"
                            defaultChecked={false}
                            endAdornment={
                                <IconeAjuda>
                                    Caso houver lançamento no dia de feriado,
                                    haverá o acréscimo das horas extras
                                    informadas
                                </IconeAjuda>
                            }
                        />
                    </Grid>,
                    <Grid item xs={12} sm={5} key="qntHrsCompensadasNaSemana">
                        <FormFieldTime
                            name="qntHrsCompensadasNaSemana"
                            label="Horas extras acrescidas no dia"
                            required={!feriadoNaSemana}
                            disabled={!feriadoNaSemana}
                        />
                    </Grid>,
                    <Grid item xs={12} key="pagarVale">
                        <FormFieldCheckbox
                            name="pagarVale"
                            label="Pagar vale alimentação"
                            defaultChecked={false}
                            endAdornment={
                                <IconeAjuda>
                                    Se marcado, o sistema irá pagar o valor do
                                    vale alimentação no dia da compensação.
                                </IconeAjuda>
                            }
                        />
                    </Grid>,
                ])
                .with(TipoOcorrencia.AMAMENTACAO, () => (
                    <Grid item xs={5}>
                        <FormFieldTime
                            name="tempoAmamentacao"
                            label="Tempo de amamentação"
                            required
                            endAdornment={
                                <IconeAjuda>
                                    <strong>
                                        Este campo controla o tempo máximo de
                                        amamentação por jornada que será
                                        considerado como tempo de trabalho.
                                    </strong>
                                    <br />
                                    <br />O artigo 396 da CLT regulamenta que a
                                    empregada tem direito a dois intervalos de
                                    30 minutos cada (totalizando uma hora por
                                    jornada), destinados para amamentar o seu
                                    filho(a) até que ele(a) complete seis meses
                                    de idade. O intervalo pode ser negociado
                                    entre a empresa e a empregada.
                                </IconeAjuda>
                            }
                        />
                    </Grid>
                ))
                .otherwise(() => null)}
            <Grid item xs={12}>
                <HorizontalDivider className="select-none">
                    <div className="inline-flex items-center">
                        Relatórios{' '}
                        <IconeAjuda>
                            Essas configurações se aplicam ao relatório de
                            absenteísmo e ao relatório de horas trabalhadas.
                        </IconeAjuda>
                    </div>
                </HorizontalDivider>
            </Grid>
            <Grid item xs={6}>
                <FormFieldCheckbox
                    name="somarAusenciasJustificadas"
                    label="Somar ausências justificadas"
                    endAdornment={
                        <IconeAjuda>
                            (Apenas para relatório de absenteísmo)
                            <br />
                            Caso marcado, essa ocorrência vai adicionar ao campo
                            "Ausências justificadas" sempre que houver um
                            lançamento no dia.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <FormFieldCheckbox
                    name="somarAusenciasInjustificadas"
                    label="Somar ausências injustificadas"
                    endAdornment={
                        <IconeAjuda>
                            (Apenas para relatório de absenteísmo)
                            <br />
                            Caso marcado, essa ocorrência vai adicionar ao campo
                            "Ausências injustificadas" sempre que houver um
                            lançamento no dia.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <FormFieldCheckbox
                    name="somarHorasTrabalhadas"
                    label="Somar horas trabalhadas"
                    endAdornment={
                        <IconeAjuda>
                            (Relatório de horas trabalhadas e absenteísmo)
                            <br />
                            Caso marcado, essa ocorrência vai adicionar ao campo
                            "Horas trabalhadas" o valor de faltas do dia em que
                            a ocorrência foi lançada.
                        </IconeAjuda>
                    }
                />
            </Grid>
        </Grid>
    )
}
